// We allow our data-access infrastructure to reference apollo-client.
// eslint-disable-next-line no-restricted-imports -- we need to use apollo-client for this
import type {MutationOptions} from "apollo-client";

import {documentNode, type GqlOperation} from "@khan/data-gql-core";
import {Errors, KAError} from "@khan/logging";
import {RequestInfo} from "@khan/shared-core";

import ApolloGqlError from "../apollo-gql-error.ts";
import type {
    ApolloCache,
    ApolloGqlClient as ApolloGqlClientInterface,
    ApolloMutationOptions,
    ApolloQueryOptions,
    ApolloResult,
    ClientContext,
    ApolloClient as InternalApolloClient,
} from "../types.ts";

import type {RefetchQueryDescription} from "./apollo-types.ts";
import createClient from "./create-client.ts";

export default class ApolloGqlClient implements ApolloGqlClientInterface {
    _testOrMockClient: InternalApolloClient | null | undefined;

    constructor(apolloClient?: InternalApolloClient) {
        this._testOrMockClient = apolloClient;
    }

    cache: (context?: Partial<ClientContext>) => ApolloCache = (context = {}) =>
        // This is a hack. Some of our cache calls have more type parameters
        // than their apollo counterparts and TypeScript is spotting that and
        // complaining, but in a really bizarre spot rather than here, where
        // the issue is introduced. So, we just cast to any to shut TypeScript up.
        this._getClient(context) as any;

    static asApolloGqlClient(
        gqlClient: ApolloGqlClientInterface,
    ): ApolloGqlClient {
        if (gqlClient instanceof ApolloGqlClient) {
            return gqlClient;
        }
        if (process.env.NODE_ENV === "test") {
            // In tests, it's probably a mocked client.
            // Either a really basic one, in which case _getClient is mocked.
            // Or an instance of GqlMockClient.
            // Let's just check that _getClient exists and leave it at that.
            if (typeof (gqlClient as any)._getClient === "function") {
                return gqlClient as any;
            }
        }
        throw new KAError("Not a ApolloGqlClient", Errors.Internal);
    }

    static getContext(context: Partial<ClientContext>): ClientContext {
        const combinedContext: ClientContext = {
            targetLocale: context.targetLocale || RequestInfo.kaLocale,
        };
        if (context.curriculum) {
            combinedContext.curriculum = context.curriculum;
        }
        return combinedContext;
    }

    _getClient: (context: Partial<ClientContext>) => InternalApolloClient = (
        context,
    ) =>
        this._testOrMockClient ||
        createClient(ApolloGqlClient.getContext(context));

    mutate: <
        TData extends Record<any, any>,
        TVariables extends Record<any, any>,
    >(
        mutation: GqlOperation<TData, TVariables>,
        options?: ApolloMutationOptions<TData, TVariables>,
    ) => Promise<ApolloResult<TData>> = <
        TData extends Record<any, any>,
        TVariables extends Record<any, any>,
    >(
        mutation: GqlOperation<TData, TVariables>,
        options?: ApolloMutationOptions<TData, TVariables>,
    ): Promise<ApolloResult<TData>> => {
        const {
            variables,
            refetchQueries,
            awaitRefetchQueries,
            optimisticResponse,
            update,
            ...localContext
        } = options || {};

        const context = ApolloGqlClient.getContext({...localContext});

        const mutationOptions: MutationOptions<TData> = {
            mutation: documentNode(mutation),
            variables,
            errorPolicy: "none",
            context,
            optimisticResponse,
            awaitRefetchQueries,
        };

        const combineRefetchQueryContext = (
            refetchQuery: RefetchQueryDescription<TData, TVariables>,
        ): RefetchQueryDescription<TData, TVariables> => {
            switch (typeof refetchQuery) {
                case "string":
                    // Apollo is going to refetch the query with the same
                    // details that were used the first time around.
                    // We can't modify the context for these.
                    // When we're not on Apollo, we will want to consider
                    // what query refetching looks like. Do we continue to
                    // "remember" based on the last call made to a query?
                    // Do we no longer support refetch? Something to consider.
                    // TODO(FEI-4223, somewhatabstract): Determine how/if we
                    // support refetching, especially string-based like this.
                    return refetchQuery;

                case "object":
                    // We can combine our context with the context
                    // defined here.
                    return {
                        ...refetchQuery,
                        context: {
                            ...context,
                            ...refetchQuery.context,
                        },
                    };

                default:
                    throw new Error("Invalid refetchQueries option");
            }
        };

        if (refetchQueries != null) {
            // Refetch queries is either an array or a function that returns an
            // array. We need to make sure that those refetch query descriptions
            // include our context, if we can.
            // NOTE: The LHS here is Apollo's typing for refetchQueries, which
            // doesn't state it allows `context` but it actually does.
            // Whereas the RHS is our version of refetchQueries, which defines
            // that `context` is optional/permitted. We must cast between them.
            if (Array.isArray(refetchQueries)) {
                mutationOptions.refetchQueries = refetchQueries.map(
                    combineRefetchQueryContext,
                ) as any;
            } else {
                // NOTE: We have to cast the args here because TypeScript sees that
                // the LHS wants an ExecutionResult<> but the RHS expects a
                // ApolloResult<>. They happen to be equivalent, so we just cast
                // to any.
                mutationOptions.refetchQueries = (...args) =>
                    // @ts-expect-error - TS2556 - A spread argument must either have a tuple type or be passed to a rest parameter.
                    refetchQueries(...(args as any)).map(
                        combineRefetchQueryContext,
                    ) as any;
            }
        }

        if (update != null) {
            mutationOptions.update = update as any;
        }

        const apolloClient = this._getClient(context);
        try {
            // We want our external API to be exact, so we need to cast
            // this inexact result.
            return (
                apolloClient.mutate<TData, TVariables>(
                    // @ts-expect-error - TS2345 - Argument of type 'MutationOptions<TData, OperationVariables>' is not assignable to parameter of type 'MutationOptions<TData, TVariables>'.
                    mutationOptions,
                ) as Promise<any>
            ).catch((e) => {
                // TODO(somewhatabstract): When we move off apollo-client, we
                // will want to throw ApolloGqlError directly, so we won't do quite
                // what we do here.
                throw new ApolloGqlError(e);
            });
        } catch (e: any) {
            // TODO(somewhatabstract): When we move off apollo-client, we will
            // want to throw ApolloGqlError directly, so we won't do quite what we
            // do here.
            throw new ApolloGqlError(e);
        }
    };

    query: <
        TData extends Record<any, any>,
        TVariables extends Record<any, any>,
    >(
        query: GqlOperation<TData, TVariables>,
        options: ApolloQueryOptions<TVariables>,
    ) => Promise<ApolloResult<TData>> = <
        TData extends Record<any, any>,
        TVariables extends Record<any, any>,
    >(
        query: GqlOperation<TData, TVariables>,
        options: ApolloQueryOptions<TVariables>,
    ): Promise<ApolloResult<TData>> => {
        const {variables, skip, fetchPolicy, ...localContext} = options;

        if (skip) {
            return Promise.resolve({
                data: undefined,
            });
        }

        const context = ApolloGqlClient.getContext({...localContext});
        const apolloClient = this._getClient(context);
        try {
            // We want our external API to be exact, so we need to cast
            // this inexact result.
            return (
                apolloClient.query({
                    query: documentNode(query),
                    variables,
                    fetchPolicy,
                    errorPolicy: "none",
                    context,
                }) as Promise<any>
            ).catch((e) => {
                // TODO(somewhatabstract): When we move off apollo-client, we
                // will want to throw ApolloGqlError directly, so we won't do quite
                // what we do here.
                throw new ApolloGqlError(e);
            });
        } catch (e: any) {
            // TODO(somewhatabstract): When we move off apollo-client, we will
            // want to throw ApolloGqlError directly, so we won't do quite what we
            // do here.
            throw new ApolloGqlError(e);
        }
    };
}
